import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Building2, DoorOpen, Cpu, Users, Bell, Sliders, Wifi, Radio,
  ChevronRight, CheckCircle2, Circle, ArrowRight, Shield, Mail, MessageSquare, FileText
} from 'lucide-react';

const STEPS = [
  {
    id: 'school',
    title: '1. Skapa skola',
    icon: Building2,
    where: 'Skolor',
    path: '/schools',
    description: 'Varje kund är en skola i systemet. Skapa en ny skola med namn, adress och kontaktuppgifter.',
    actions: [
      'Gå till Skolor i sidomenyn',
      'Klicka "Lägg till skola"',
      'Fyll i skolans namn, adress och kontakt-e-post',
      'Välj ML-känslighet (rekommenderat: "medium" för nya kunder)',
      'Spara',
    ],
    tips: 'Cooldown-minuter styr hur länge systemet väntar mellan larm från samma sensor. Standard 10 min räcker för de flesta.',
  },
  {
    id: 'rooms',
    title: '2. Skapa rum',
    icon: DoorOpen,
    where: 'Rum',
    path: '/rooms',
    description: 'Skapa ett rum för varje plats där en sensor ska placeras. Rum kopplas till skolan.',
    actions: [
      'Gå till Rum i sidomenyn',
      'Klicka "Lägg till rum"',
      'Välj rätt skola i dropdown',
      'Ange rumsnamn (t.ex. "Elevtoalett Plan 1", "Omklädningsrum Gym")',
      'Valfritt: ange våning, byggnad och rumstyp',
      'Spara — upprepa för varje sensorplacering',
    ],
    tips: 'Namnge rummen tydligt — personalen ser dessa namn i larm och notifieringar.',
  },
  {
    id: 'devices',
    title: '3. Registrera sensorer',
    icon: Cpu,
    where: 'Enheter',
    path: '/devices',
    description: 'Lägg till sensorerna — välj WiFi eller LoRaWAN beroende på sensortyp.',
    actions: [
      'Gå till Enheter i sidomenyn',
      'Klicka "Lägg till enhet"',
      'Välj anslutningstyp: WiFi eller LoRaWAN',
    ],
    subsections: [
      {
        title: 'WiFi-sensor (AirPoint-1)',
        icon: Wifi,
        steps: [
          'Välj WiFi i dialogen',
          'Ange Sensor-ID (samma som MQTT Client ID i sensorns konfiguration, t.ex. "sensor001")',
          'Ge sensorn ett beskrivande namn',
          'Välj rätt rum',
          'Klicka "Skapa enhet"',
          'Konfigurera sensorn: MQTT Server = 136.113.191.59, Port = 1883',
          'Username och Password fås från Vapelarm API (app.vapelarm.se)',
        ],
      },
      {
        title: 'LoRaWAN-sensor (FS00802G)',
        icon: Radio,
        steps: [
          'Välj LoRaWAN i dialogen',
          'Ange DevEUI (16 hex-tecken från sensorns etikett)',
          'Ge sensorn ett beskrivande namn',
          'Välj rätt rum',
          'Klicka "Skapa enhet"',
          'Konfigurera TTN webhook: Base URL = https://cloud.vapelarm.se/api',
          'Uplink path = /lorawan/uplink/raw',
        ],
      },
    ],
    tips: 'Sensorer som skickar data auto-registreras om de inte redan finns. Du kan flytta dem till rätt rum efteråt.',
  },
  {
    id: 'users',
    title: '4. Skapa användarkonton',
    icon: Users,
    where: 'Användare',
    path: '/users',
    description: 'Skapa konton för skolans personal. Varje roll har olika behörigheter.',
    actions: [
      'Gå till Användare i sidomenyn',
      'Klicka "Lägg till användare"',
      'Fyll i namn, e-post och lösenord',
      'Välj roll och koppla till rätt skola',
    ],
    subsections: [
      {
        title: 'Roller',
        icon: Shield,
        steps: [
          'Skoladmin — Ser händelser, analys och kan hantera personal på sin skola',
          'Personal (Staff) — Mobilvy med pager-larm, kvittering av händelser',
          'Kommunadmin — Läsåtkomst till alla skolor, aggregerad statistik',
        ],
      },
    ],
    tips: 'Personalkonton (Staff) ser bara pager-vyn — perfekt för vaktmästare och rastvakter som bara behöver reagera på larm.',
  },
  {
    id: 'notifications',
    title: '5. Konfigurera notifieringar',
    icon: Bell,
    where: 'Aviseringar',
    path: '/notification-rules',
    description: 'Ställ in hur och när larm ska skickas. E-post och Teams stöds.',
    actions: [
      'Gå till Aviseringar i sidomenyn',
      'Klicka "Lägg till regel"',
      'Välj skola',
      'Välj kanal: E-post eller Microsoft Teams',
      'Ange mottagare (e-postadress eller Teams webhook-URL)',
      'Välj vilka händelsetyper som ska trigga: Vape, Misstanke, Offline',
      'Spara',
    ],
    subsections: [
      {
        title: 'E-post (SendGrid)',
        icon: Mail,
        steps: [
          'Ange mottagarens e-postadress',
          'Larm skickas från alerts@vapelarm.se',
          'Svensk e-postmall med skola, rum, sensor och analysdetaljer',
        ],
      },
      {
        title: 'Microsoft Teams',
        icon: MessageSquare,
        steps: [
          'Skapa en Incoming Webhook i Teams-kanalen',
          'Klistra in webhook-URL:en i Vapelarm',
          'Larm visas som kort i Teams med allvarlighetsgrad och detaljer',
        ],
      },
    ],
    tips: 'Skapa minst en e-postregel per skola. Tipsa kunden att lägga till en Teams-kanal för realtidslarm till personalgruppen.',
  },
  {
    id: 'alarm-profiles',
    title: '6. Justera larmprofiler (valfritt)',
    icon: Sliders,
    where: 'Larmprofiler',
    path: '/alarm-profiles',
    description: 'Larmprofiler styr hur känslig detektionen är. Standard ("medium") passar de flesta.',
    actions: [
      'Gå till Larmprofiler i sidomenyn',
      'Välj profil: Lenient (få larm), Medium (balanserad) eller Strict (känslig)',
      'Eller skapa en Custom-profil med egna tröskelvärden',
      'Koppla profilen till skolan under Skolor → Redigera',
    ],
    tips: 'Börja med "medium" och justera efter en veckas data. Om skolan rapporterar för många falsklarm — byt till "lenient".',
  },
  {
    id: 'verify',
    title: '7. Verifiera installationen',
    icon: CheckCircle2,
    where: 'Enheter',
    path: '/devices',
    description: 'Kontrollera att allt fungerar innan du lämnar över till kunden.',
    actions: [
      'Gå till Enheter — alla sensorer ska visa "Online"',
      'Klicka på en sensor → Trigga testalarm (knappen "Testalarm")',
      'Kontrollera att e-post/Teams-notifikation kom fram',
      'Gå till Översikt — dashboarden ska visa skolans data',
      'Gå till Händelser — testlarmet ska synas',
      'Logga in som Skoladmin och verifiera att de ser rätt data',
      'Logga in som Staff och verifiera att pager-vyn fungerar',
    ],
    tips: 'Skicka kundens skoladmin inloggningsuppgifterna och länken: https://cloud.vapelarm.se',
  },
];

function StepCard({ step, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-4 rounded-lg border transition-all",
        isActive
          ? "border-navy bg-navy/5 shadow-sm"
          : "border-slate-200 hover:border-slate-300 bg-white"
      )}
      data-testid={`guide-step-${step.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={cn(
          "h-9 w-9 rounded-md flex items-center justify-center shrink-0",
          isActive ? "bg-navy text-white" : "bg-slate-100 text-slate-500"
        )}>
          <step.icon className="h-4.5 w-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={cn("font-ibm font-semibold text-sm", isActive ? "text-navy" : "text-slate-700")}>
            {step.title}
          </p>
          <p className="text-xs text-slate-400 truncate">{step.where}</p>
        </div>
        <ChevronRight className={cn("h-4 w-4 shrink-0", isActive ? "text-navy" : "text-slate-300")} />
      </div>
    </button>
  );
}

export default function AdminGuidePage() {
  const [activeStep, setActiveStep] = useState('school');
  const step = STEPS.find(s => s.id === activeStep) || STEPS[0];

  return (
    <div className="p-6 md:p-10" data-testid="admin-guide-page">
      <div className="mb-8">
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
          Ny kund — Uppsättningsguide
        </h1>
        <p className="text-slate-500 mt-1 font-ibm">
          Steg-för-steg: från ny skola till fullt fungerande vape-detektion
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Step list */}
        <div className="space-y-2">
          {STEPS.map(s => (
            <StepCard key={s.id} step={s} isActive={activeStep === s.id} onClick={() => setActiveStep(s.id)} />
          ))}
        </div>

        {/* Active step detail */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-md bg-navy text-white flex items-center justify-center">
                <step.icon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="font-chivo text-xl">{step.title}</CardTitle>
                <p className="text-sm text-slate-500 font-ibm flex items-center gap-1 mt-0.5">
                  <ArrowRight className="h-3 w-3" /> {step.where}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-slate-600 font-ibm">{step.description}</p>

            {/* Main actions */}
            <div className="space-y-2">
              <h3 className="text-xs font-ibm uppercase tracking-widest text-slate-400 font-semibold">Gör detta</h3>
              <ol className="space-y-2">
                {step.actions.map((action, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-ibm">
                    <span className="h-5 w-5 rounded-full bg-navy/10 text-navy text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-slate-700">{action}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Subsections */}
            {step.subsections && step.subsections.map((sub, i) => (
              <div key={i} className="border border-slate-100 rounded-lg p-4 bg-slate-50/50">
                <div className="flex items-center gap-2 mb-3">
                  <sub.icon className="h-4 w-4 text-navy" />
                  <h4 className="font-ibm font-semibold text-sm text-slate-800">{sub.title}</h4>
                </div>
                <ul className="space-y-1.5">
                  {sub.steps.map((s, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm font-ibm text-slate-600">
                      <Circle className="h-1.5 w-1.5 mt-2 shrink-0 fill-slate-400 text-slate-400" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Tip */}
            {step.tips && (
              <div className="flex gap-3 p-4 bg-amber-50 border border-amber-100 rounded-lg">
                <span className="text-amber-500 text-lg shrink-0">Tips</span>
                <p className="text-sm font-ibm text-amber-800">{step.tips}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
